import { Dropdown, message } from 'antd';
import {
  UndoOutlined,
  RedoOutlined,
  CopyOutlined,
  ScissorOutlined,
  DeleteOutlined,
  AlignCenterOutlined,
  OrderedListOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import useSceneStore from '../../../stores/sceneStore';
import useHistoryStore from '../../../stores/historyStore';
import useSelectionStore from '../../../stores/selectionStore';
import { createModel } from '../../../api/models';
import { deleteModelsByIds } from '../../../utils/modelActions';
import { getSubObjectClipboard } from '../../../stores/subObjectMenuStore';
import { copySubObject, cutSubObject, pasteSubObject, deleteSelectedSubObjects } from '../../../utils/subObjectOps';

/**
 * 左上角「操作」菜单
 * 2026-09-14：与右键上下文菜单同步——选中部件（含多选/同源批量）时，复制/剪切/粘贴/删除走部件逻辑；
 * 未选部件时保持模型级逻辑。修复：粘贴"服务器创建 + 本地 pasteModel 再加一份"导致的双创建。
 */
export default function OperationMenu() {
  const { undo, redo, selectedModelId, models, currentScene, addModel, clipboardModels } = useSceneStore();
  const { selectedIds } = useSelectionStore();
  const { canUndo, canRedo } = useHistoryStore();
  // 2026-09-14：部件选中态（单选/多选——与右键菜单同步）
  const selectedSubObjects = useSceneStore((s) => s.selectedSubObjects);
  const selectedSubObject = useSceneStore((s) => s.selectedSubObject);
  const hasSubSel = selectedSubObjects.length > 0 || !!selectedSubObject;
  const subClipboard = getSubObjectClipboard();

  const handleMenuClick = async (e: { key: string }) => {
    const st = useSceneStore.getState();
    const subList = st.selectedSubObjects.length > 0
      ? st.selectedSubObjects
      : (st.selectedSubObject ? [st.selectedSubObject] : []);
    const subMode = subList.length > 0;

    switch (e.key) {
      case 'undo':
        undo();
        break;
      case 'redo':
        redo();
        break;
      case 'copy': {
        // 部件模式：逐个克隆（材质深拷贝、挂同父；多选=批量复制）
        if (subMode) {
          subList.forEach((x) => copySubObject(x.modelId, x.uuid));
          if (subList.length > 1) message.success(`已复制 ${subList.length} 个部件`);
          break;
        }
        if (!selectedModelId && selectedIds.length === 0) return;
        // 多选批量复制：逐个创建副本（服务器 + 本地），偏移递增避免重叠
        const srcIds = selectedIds.length > 0 ? selectedIds : [selectedModelId];
        const srcs = srcIds.map((id) => models.find((m) => m.id === id)).filter((m) => m !== undefined);
        if (srcs.length === 0) return;
        try {
          const created = [];
          for (let i = 0; i < srcs.length; i++) {
            const src = srcs[i];
            const clone = await createModel({
              sceneId: src.sceneId,
              name: `${src.name} 副本`,
              url: src.url,
              format: src.format,
              position: [src.position[0] + 2 + i * 0.5, src.position[1], src.position[2] + i * 0.5],
              rotation: [...src.rotation],
              scale: [...src.scale],
              visible: src.visible,
              materials: src.materials,
            });
            addModel({
              id: clone.id,
              name: clone.name,
              url: clone.url,
              format: clone.format,
              position: clone.position,
              rotation: clone.rotation,
              scale: clone.scale,
              visible: clone.visible,
              materials: clone.materials,
            });
            created.push(clone.id);
          }
          if (created.length > 1) {
            useSelectionStore.getState().selectAll(created);
            message.success(`已复制 ${created.length} 个模型`);
          } else {
            message.success('已复制模型');
          }
        } catch {
          message.error('复制失败');
        }
        break;
      }
      case 'cut': {
        // 部件模式：逐个软隐藏（剪贴板记录，Ctrl+V/Ctrl+Z 恢复）
        if (subMode) {
          subList.forEach((x) => cutSubObject(x.modelId, x.uuid));
          if (subList.length > 1) message.success(`已剪切 ${subList.length} 个部件（Ctrl+V 恢复）`);
          break;
        }
        if (!selectedModelId && selectedIds.length === 0) return;
        const srcIds = (selectedIds.length > 0 ? selectedIds : [selectedModelId]).filter((x): x is string => !!x);
        srcIds.forEach((id) => useSceneStore.getState().cutModel(id));
        message.success(`已剪切 ${srcIds.length} 个（可粘贴）`);
        break;
      }
      case 'paste': {
        // 部件剪贴板优先（与右键菜单一致）
        if (subClipboard) {
          pasteSubObject();
          break;
        }
        if (!clipboardModels.length) return;
        const src = clipboardModels[0];
        try {
          // 修复：原实现 createModel（服务器）+ pasteModel（本地再加一份）双创建；
          // 现与"复制"一致：服务器创建拿 id → addModel 本地加入 → 清空剪贴板
          const clone = await createModel({
            sceneId: src.sceneId || currentScene?.id || '',
            name: src.name,
            url: src.url,
            format: src.format,
            presetId: src.presetId,
            position: [src.position[0] + 2, src.position[1], src.position[2]],
            rotation: [...src.rotation],
            scale: [...src.scale],
            visible: src.visible,
            materials: src.materials,
          });
          addModel({
            id: clone.id,
            name: clone.name,
            url: clone.url,
            format: clone.format,
            position: clone.position,
            rotation: clone.rotation,
            scale: clone.scale,
            visible: clone.visible,
            materials: clone.materials,
          });
          useSceneStore.getState().clearClipboard();
          message.success('已粘贴');
        } catch {
          message.error('粘贴失败');
        }
        break;
      }
      case 'delete': {
        // 部件模式：批量软删除（确认 + 快照可撤销）
        if (subMode) {
          deleteSelectedSubObjects();
          break;
        }
        const ids = selectedIds.length > 0 ? selectedIds : (selectedModelId ? [selectedModelId] : []);
        deleteModelsByIds(ids);
        break;
      }
      case 'align-center': {
        if (hasSubSel) {
          message.info('对齐仅适用于模型（部件选中时不可用）');
          break;
        }
        const targets = selectedIds.length > 0
          ? selectedIds.map((id) => models.find((m) => m.id === id)).filter((m) => m !== undefined)
          : (selectedModelId ? models.filter((m) => m.id === selectedModelId) : []);
        if (!targets.length) return;
        const store = useSceneStore.getState();
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        targets.forEach((m) => {
          minX = Math.min(minX, m.position[0]);
          maxX = Math.max(maxX, m.position[0]);
          minY = Math.min(minY, m.position[1]);
          maxY = Math.max(maxY, m.position[1]);
          minZ = Math.min(minZ, m.position[2]);
          maxZ = Math.max(maxZ, m.position[2]);
        });
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        const cz = (minZ + maxZ) / 2;
        targets.forEach((m) => {
          store.updateModel(m.id, { position: [m.position[0] - cx, m.position[1] - cy, m.position[2] - cz] }, false);
        });
        Promise.allSettled(
          targets.map((m) =>
            import('../../../api/models').then(({ updateModel: apiUpdateModel }) =>
              apiUpdateModel(m.id, { position: m.position }).catch(() => {}))
          )
        );
        message.success('已对齐');
        break;
      }
      case 'arrange': {
        if (hasSubSel) {
          message.info('排列仅适用于模型（部件选中时不可用）');
          break;
        }
        const allModels = useSceneStore.getState().models.filter((m) => m.visible);
        const cols = Math.ceil(Math.sqrt(allModels.length));
        const spacing = 3;
        const startX = -(((cols - 1) * spacing) / 2);
        allModels.forEach((m, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          useSceneStore.getState().updateModel(m.id, {
            position: [startX + col * spacing, 0, row * spacing],
          }, false);
        });
        message.success('已自动排列');
        break;
      }
      default:
        break;
    }
  };

  const menuItems = [
    {
      key: 'undo',
      icon: <UndoOutlined />,
      label: '撤销 (Ctrl+Z)',
      disabled: !canUndo,
    },
    {
      key: 'redo',
      icon: <RedoOutlined />,
      label: '重做 (Ctrl+Y)',
      disabled: !canRedo,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'copy',
      icon: <CopyOutlined />,
      label: hasSubSel
        ? (selectedSubObjects.length > 1 ? `复制部件（${selectedSubObjects.length}）` : '复制部件')
        : (selectedIds.length > 1 ? `复制（${selectedIds.length}）` : '复制'),
      disabled: !hasSubSel && !selectedModelId && selectedIds.length === 0,
    },
    {
      key: 'cut',
      icon: <ScissorOutlined />,
      label: hasSubSel
        ? (selectedSubObjects.length > 1 ? `剪切部件（${selectedSubObjects.length}）` : '剪切部件')
        : (selectedIds.length > 1 ? `剪切（${selectedIds.length}）` : '剪切'),
      disabled: !hasSubSel && selectedIds.length === 0 && !selectedModelId,
    },
    {
      key: 'paste',
      icon: <SnippetsOutlined />,
      label: subClipboard ? `粘贴部件「${subClipboard.name}」` : '粘贴',
      disabled: !subClipboard && clipboardModels.length === 0,
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: hasSubSel
        ? (selectedSubObjects.length > 1 ? `删除部件（${selectedSubObjects.length}）` : '删除部件')
        : (selectedIds.length > 1 ? `删除（${selectedIds.length}）` : '删除'),
      danger: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'align-center',
      icon: <AlignCenterOutlined />,
      label: '对齐（模型）',
      disabled: hasSubSel,
    },
    {
      key: 'arrange',
      icon: <OrderedListOutlined />,
      label: '排列（模型）',
      disabled: hasSubSel,
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleMenuClick }}
      trigger={['click']}
    >
      <span style={{ cursor: 'pointer', padding: '0 8px' }}>
        操作▼
      </span>
    </Dropdown>
  );
}
